// src/pages/Home/ClubPartners.jsx
import React, { useEffect, useMemo, useState } from "react";
import { getAllAssociations } from "../../services/associationsServices";
import { MapContainer, Marker, Popup, TileLayer, useMap } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import {
  FaUsers,
  FaMapMarkerAlt,
  FaLayerGroup,
  FaDumbbell,
  FaGlobe,
  FaHandsHelping,
} from "react-icons/fa";
import Reveal from "../../components/ui/Reveal";

// Fix Leaflet marker icon paths
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl:
    "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png",
  iconUrl:
    "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png",
  shadowUrl:
    "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png",
});

const cx = (...c) => c.filter(Boolean).join(" ");

const normalize = (value = "") =>
  String(value)
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");

const FRANCE_METRO_BOUNDS = [
  [41.0, -5.5], // sud-ouest
  [51.5, 9.8], // nord-est
];

const isInMetropolitanFrance = (lat, lng) => {
  return lat >= 41.0 && lat <= 51.5 && lng >= -5.5 && lng <= 9.8;
};

function StatCard({ icon, label, value, helper }) {
  return (
    <div
      className={cx(
        "rounded-[28px] border border-slate-200 bg-white p-5 shadow-sm",
        "dark:border-white/10 dark:bg-slate-900",
      )}
    >
      <div className="flex items-center gap-3">
        <div className="grid h-11 w-11 place-items-center rounded-2xl bg-sky-100 text-sky-600 dark:bg-sky-500/10 dark:text-sky-400">
          {icon}
        </div>
        <div>
          <div className="text-sm font-medium text-slate-500 dark:text-slate-400">
            {label}
          </div>
          <div className="text-2xl font-bold text-slate-900 dark:text-white">
            {value}
          </div>
        </div>
      </div>

      {helper && (
        <p className="mt-4 text-sm leading-6 text-slate-600 dark:text-slate-300">
          {helper}
        </p>
      )}
    </div>
  );
}

function SelectField({ label, value, onChange, options, allLabel = "Tous" }) {
  return (
    <div>
      <label className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">
        {label}
      </label>

      <select
        value={value}
        onChange={onChange}
        className={cx(
          "mt-2 w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-slate-900 outline-none transition",
          "focus:ring-2 focus:ring-sky-400/70",
          "dark:border-white/10 dark:bg-slate-900 dark:text-white",
        )}
      >
        <option value="">{allLabel}</option>
        {options.map((item) => (
          <option key={item} value={item}>
            {item}
          </option>
        ))}
      </select>
    </div>
  );
}

function MapAutoFit({ points }) {
  const map = useMap();

  useEffect(() => {
    if (!points.length) {
      map.fitBounds(FRANCE_METRO_BOUNDS, { padding: [20, 20] });
      return;
    }

    if (points.length === 1) {
      map.setView(points[0], 8);
      return;
    }

    const bounds = L.latLngBounds(points);
    map.fitBounds(bounds, { padding: [40, 40] });
  }, [map, points]);

  return null;
}

function LoadingState() {
  return (
    <div className="space-y-8">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <div
            key={i}
            className="rounded-[28px] border border-slate-200 bg-white p-5 shadow-sm dark:border-white/10 dark:bg-slate-900"
          >
            <div className="h-5 w-32 rounded bg-slate-200 animate-pulse dark:bg-slate-800" />
            <div className="mt-4 h-8 w-16 rounded bg-slate-200 animate-pulse dark:bg-slate-800" />
            <div className="mt-4 h-4 w-full rounded bg-slate-100 animate-pulse dark:bg-slate-800/80" />
          </div>
        ))}
      </div>

      <div className="rounded-[32px] border border-slate-200 bg-white p-5 shadow-sm dark:border-white/10 dark:bg-slate-900">
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
          <div className="h-14 rounded-2xl bg-slate-100 animate-pulse dark:bg-slate-800" />
          <div className="h-14 rounded-2xl bg-slate-100 animate-pulse dark:bg-slate-800" />
          <div className="h-14 rounded-2xl bg-slate-100 animate-pulse dark:bg-slate-800" />
        </div>

        <div className="mt-5 h-[500px] rounded-[28px] bg-slate-100 animate-pulse dark:bg-slate-800" />
      </div>
    </div>
  );
}

const ClubPartners = () => {
  const [clubs, setClubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDiscipline, setSelectedDiscipline] = useState("");
  const [selectedProduit, setSelectedProduit] = useState("");

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        const all = await getAllAssociations();
        const favorites = (Array.isArray(all) ? all : []).filter(
          (asso) =>
            asso.is_favorite &&
            ["Clients MAC", "Clients Website", "Client MAL","En Test", "Clients SAAS", "Clients MAM"].includes(asso.statut_asso),
        );

        setClubs(favorites);
      } catch (error) {
        console.error(
          "Erreur lors du chargement des associations favorites :",
          error,
        );
        setClubs([]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const disciplines = useMemo(() => {
    const set = new Set(
      clubs.flatMap((club) =>
        String(club.discipline_asso || "")
          .split(",")
          .map((x) => x.trim())
          .filter(Boolean),
      ),
    );
    return [...set].sort((a, b) => a.localeCompare(b));
  }, [clubs]);

  const produits = useMemo(() => {
    const set = new Set(clubs.map((club) => club.produit_asso).filter(Boolean));
    return [...set].sort((a, b) => a.localeCompare(b));
  }, [clubs]);

  const filteredClubs = useMemo(() => {
    return clubs.filter((club) => {
      const matchDiscipline = selectedDiscipline
        ? normalize(club.discipline_asso).includes(
            normalize(selectedDiscipline),
          )
        : true;

      const matchProduit = selectedProduit
        ? club.produit_asso === selectedProduit
        : true;

      return matchDiscipline && matchProduit;
    });
  }, [clubs, selectedDiscipline, selectedProduit]);

  const mappableClubs = useMemo(() => {
    return filteredClubs.filter(
      (club) =>
        club.latitude &&
        club.longitude &&
        !Number.isNaN(parseFloat(club.latitude)) &&
        !Number.isNaN(parseFloat(club.longitude)),
    );
  }, [filteredClubs]);

  const mapPointsFrance = useMemo(() => {
    return mappableClubs
      .map((club) => [parseFloat(club.latitude), parseFloat(club.longitude)])
      .filter(([lat, lng]) => isInMetropolitanFrance(lat, lng));
  }, [mappableClubs]);

  const stats = useMemo(() => {
    const websitesCount = clubs.filter((club) => !!club.siteweb_asso).length;

    return {
      totalPartners: clubs.length,
      disciplinesCount: disciplines.length,
      productsCount: produits.length,
      mappedCount: mappableClubs.length,
      websitesCount,
    };
  }, [clubs, disciplines.length, produits.length, mappableClubs.length]);

  if (loading) {
    return <LoadingState />;
  }

  return (
    <div className="space-y-8">
      <Reveal>
        <div className="inline-flex max-w-full items-center gap-3 rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-bold text-slate-800 shadow-sm dark:border-white/10 dark:bg-slate-900 dark:text-white">
          <span className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-orange-500 text-white">
            <FaHandsHelping />
          </span>
          <span className="truncate">Ils me font confiance</span>
        </div>

        <h2 className="mt-6 max-w-2xl text-3xl font-bold leading-tight tracking-tight text-slate-950 dark:text-white sm:text-4xl lg:text-5xl">
          Des clubs et collectivités.
          <span className="block text-[#004b86] dark:text-sky-300">
            déjà équipés
          </span>
        </h2>
        <p className="mt-4 max-w-3xl text-base leading-7 text-slate-600 dark:text-slate-300 sm:text-lg">
          Un aperçu des structures qui utilisent mes applications au quotidien.
        </p>
      </Reveal>
      <Reveal>
        {/* Stats */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3 xl:grid-cols-3 mb-4">
          <StatCard
            icon={<FaUsers />}
            label="Structures partenaires"
            value={stats.totalPartners}
            helper="sur l'ensemble du territoire français, Belgique et DomTom."
          />

          <StatCard
            icon={<FaDumbbell />}
            label="Disciplines"
            value={stats.disciplinesCount}
            helper="Des usages répartis sur plusieurs pratiques sportives."
          />

          <StatCard
            icon={<FaLayerGroup />}
            label="Solutions déployées"
            value={stats.productsCount}
            helper="Différentes applications déjà utilisées sur le terrain."
          />

          {/* <StatCard
            icon={<FaMapMarkerAlt />}
            label="Clubs visibles sur la carte"
            value={stats.mappedCount}
            helper="Structures géolocalisées selon les filtres sélectionnés."
          /> */}
        </div>

        {/* Filters + map */}
        <div className="rounded-[32px] border border-slate-200 bg-white p-5 shadow-sm dark:border-white/10 dark:bg-slate-900 sm:p-6">
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-[1fr_1fr_auto] lg:items-end">
            <SelectField
              label="Discipline"
              value={selectedDiscipline}
              onChange={(e) => setSelectedDiscipline(e.target.value)}
              options={disciplines}
              allLabel="Toutes les disciplines"
            />

            <SelectField
              label="Solution"
              value={selectedProduit}
              onChange={(e) => setSelectedProduit(e.target.value)}
              options={produits}
              allLabel="Toutes les solutions"
            />

            <div className="rounded-2xl bg-slate-50 px-4 py-3 text-sm text-slate-600 dark:bg-slate-800 dark:text-slate-300">
              <span className="font-semibold text-slate-900 dark:text-white">
                {filteredClubs.length}
              </span>{" "}
              structure{filteredClubs.length > 1 ? "s" : ""} sélectionnée
              {filteredClubs.length > 1 ? "s" : ""}
            </div>
          </div>

          <div className="mt-5 overflow-hidden rounded-[28px] border border-slate-200 dark:border-white/10">
            {mappableClubs.length ? (
              <div className="h-[420px] sm:h-[500px]">
                <MapContainer
                  center={[46.603354, 1.888334]}
                  zoom={6}
                  scrollWheelZoom
                  maxBounds={FRANCE_METRO_BOUNDS}
                  maxBoundsViscosity={0.8}
                  className="h-full w-full"
                >
                  <TileLayer
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    attribution="© OpenStreetMap contributors"
                  />

                  <MapAutoFit points={mapPointsFrance} />

                  {mappableClubs.map((club) => {
                    const lat = parseFloat(club.latitude);
                    const lng = parseFloat(club.longitude);

                    return (
                      <Marker key={club.id} position={[lat, lng]}>
                        <Popup>
                          <div className="max-w-[230px] text-sm leading-6">
                            <div className="font-semibold text-slate-900">
                              {club.nom_asso}
                            </div>

                            {club.discipline_asso && (
                              <div className="mt-1 text-slate-700">
                                {club.discipline_asso}
                              </div>
                            )}

                            {club.produit_asso && (
                              <div className="mt-2 text-slate-700">
                                <span className="font-medium">Solution :</span>{" "}
                                {club.produit_asso}
                              </div>
                            )}

                            {club.adresse_asso && (
                              <div className="mt-2 text-slate-700">
                                <span className="font-medium">Adresse :</span>{" "}
                                {club.adresse_asso}
                              </div>
                            )}

                            {club.siteweb_asso && (
                              <a
                                href={club.siteweb_asso}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="mt-3 inline-flex items-center gap-2 font-medium text-sky-600 underline"
                              >
                                <FaGlobe />
                                Site web
                              </a>
                            )}
                          </div>
                        </Popup>
                      </Marker>
                    );
                  })}
                </MapContainer>
              </div>
            ) : (
              <div className="flex min-h-[320px] items-center justify-center bg-slate-50 px-6 text-center dark:bg-slate-800">
                <div>
                  <div className="text-lg font-semibold text-slate-900 dark:text-white">
                    Aucun club géolocalisé avec ces filtres
                  </div>
                  <p className="mt-2 text-sm leading-7 text-slate-600 dark:text-slate-300">
                    Modifie les filtres pour afficher d’autres structures sur la
                    carte.
                  </p>
                </div>
              </div>
            )}
          </div>

          <div className="mt-4 text-sm text-slate-500 dark:text-slate-400">
            Cette carte met en avant les structures partenaires et leur
            répartition géographique.
          </div>
        </div>
      </Reveal>
    </div>
  );
};

export default ClubPartners;
